import AspectRatio from '@blocks/AspectRatio'
import Button from '@blocks/Button'
import EmphasizedContent from '@blocks/EmphasizedContent'
import Flex from '@blocks/Flex'
import Footer, { FooterContacts, FooterDevelopedBy } from '@blocks/Footer'
import Grid, { GridProps } from '@blocks/Grid'
import UnexpectedSalonTypeError from '@errors/UnexpectedSalonTypeError'
import useSalon from '@general/models/Salon'
import { formatDateToMonthAndDay, formatJuryName, route } from '@helpers'
import useTitle from '@hooks/useTitle'
import { Container } from '@styles/helpers'
import { desktop, tablet } from '@styles/mixins'
import { colors, grid } from '@styles/vars'
import React from 'react'
import { useParams } from 'react-router-dom'
import styled, { css } from 'styled-components'
import Header from './components/SalonLandingHeader'
import Section from './components/SalonLandingSection'
import SalonMainScreenAction from './components/SalonMainScreenAction'
import SalonContext, { useSalonContext } from './context'
import { ReactComponent as DownloadIcon } from '@icons/download.svg'
import NudityNotification from '@blocks/NudityNotification'

export default function SalonPage() {
    const { slug } = useParams<{ slug: string }>()
    const salon = useSalon(slug)

    const name = salon.state?.name || "Loading..."
    useTitle(`Salon: ${name}`)

    if (!salon.state)
        return null

    return (
        <SalonContext.Provider value={{ salon } as General.Contexts.Salon}>
            <Grid gap="30px|60px">
                <MainScreen />
                <Statistics />
                <Nudity />
                <Dates />
                <Sections />
                <Judges />
                <About />
                <CallToAction />
                <Footer />
            </Grid>
        </SalonContext.Provider>
    )
}

// COMPONENTS
const Nudity = () => {
    const { salon } = useSalonContext()

    if (!salon.state.nudity_restricted)
        return null

    if (salon.state.type !== "current" && salon.state.type !== "future")
        return null

    return (
        <Container>
            <Grid>
                <h2>Important</h2>
                <NudityNotification />
            </Grid>
        </Container>
    )
}

const MainScreen = () => {
    const { salon } = useSalonContext()

    return (
        <MainScreenWrapper>
            <HeaderWrapper>
                <Header />
            </HeaderWrapper>
            <BackgroundWrapper background={salon.state.background_url} />
            <MainScreenContainer>
                <Grid gap="20px|30px">
                    <Grid gap="20px">
                        <Flex gap="10px" flexWrap="wrap">
                            {salon.attrs.organizations().array.map(item => <OrganizationLogo key={item.id}><img src={item.logo_url} /></OrganizationLogo>)}
                        </Flex>
                        <Grid gap="10px">
                            <h1>{salon.state.name}</h1>
                        </Grid>
                        <Flex gap="15px" flexWrap="wrap">
                            <div>
                                <Grid gap="5px">
                                    <h4>Country</h4>
                                    <p>{salon.state.country.name}</p>
                                </Grid>
                            </div>
                            {salon.state.patronages.map(patronage => (
                                <div key={patronage.id}>
                                    <Grid gap="5px">
                                        <h4>{patronage.organization.name}</h4>
                                        <p>{patronage.number}</p>
                                    </Grid>
                                </div>
                            ))}
                        </Flex>
                    </Grid>
                    <ButtonsContainer>
                        <SalonMainScreenAction />
                        <SalonMainScreenAction type="secondary" />
                    </ButtonsContainer>
                </Grid>
            </MainScreenContainer>
        </MainScreenWrapper>
    )
}

const Statistics = () => {
    const { salon } = useSalonContext()

    const isFinished = salon.state.type === "finished"

    return (
        <Container>
            <Grid columns="3">
                <EmphasizedContent>
                    <Grid gap="15px">
                        <h3>Status List</h3>
                        <Flex justifyContent="flex-start"><Button type="secondary" width="auto" element="a" target="_blank" to={route("general:salon:status", salon.state.slug)}>See List</Button></Flex>
                    </Grid>
                </EmphasizedContent>
                <EmphasizedContent style={{ opacity: isFinished ? 1 : 0.6 }}>
                    <Grid gap="15px">
                        <Grid gap="5px">
                            <h3>Acceptances List</h3>
                            {!isFinished && <p>Available after results date.</p>}
                        </Grid>
                        {isFinished &&
                            <Flex justifyContent="flex-start">
                                <Button type="secondary" width="auto" element="a" target="_blank" to={route("general:salon:acceptances", salon.state.slug)}>See List</Button>
                            </Flex>
                        }
                    </Grid>
                </EmphasizedContent>
                <EmphasizedContent style={{ opacity: isFinished ? 1 : 0.6 }}>
                    <Grid gap="15px">
                        <Grid gap="5px">
                            <h3>Awards List</h3>
                            {!isFinished && <p>Available after results date.</p>}
                        </Grid>
                        {isFinished &&
                            <Flex justifyContent="flex-start">
                                <Button type="secondary" width="auto" element="a" target="_blank" to={route("general:salon:awards", salon.state.slug)}>See List</Button>
                            </Flex>
                        }
                    </Grid>
                </EmphasizedContent>
                {salon.state.ebook && (
                    <Grid>
                        <EmphasizedContent>
                            <Grid gap="15px">
                                <h3>Ebook</h3>
                                <Flex justifyContent="flex-start">
                                    <Button icon={<DownloadIcon />} type="secondary" width="auto" onClick={salon.api.ebook}>Download</Button>
                                </Flex>
                            </Grid>
                        </EmphasizedContent>
                    </Grid>
                )}
            </Grid>
        </Container>
    )
}

const Dates = () => {
    const { salon } = useSalonContext()

    return (
        <Container id="dates">
            <Grid>
                <h2>Calendar</h2>
                <Grid columns="4">
                    <Grid gap="10px">
                        <h3>Start | {formatDateToMonthAndDay(salon.state.start_date)}</h3>
                        <p>Entries open on <a href="https://isalons.net">isalons.net</a> platform. Please, log in / register to participate. </p>
                    </Grid>
                    <Grid gap="10px">
                        <h3>Closing | {formatDateToMonthAndDay(salon.state.closing_date)}</h3>
                        <p>We will send you final reminder by email and separate email if you have not finalized your entry. We can extend a deadline for a few days if you notify us.</p>
                    </Grid>
                    <Grid gap="10px">
                        <h3>Results | {formatDateToMonthAndDay(salon.state.results_date)}</h3>
                        <p>100% on time for the last 5 years. You will get results by email & on salon web site.</p>
                    </Grid>
                    <Grid gap="10px">
                        <h3>Awards | {formatDateToMonthAndDay(salon.state.awards_date)}</h3>
                        <p>Awards are safely packed and sent to the provided address. You will get an email with tracking number. Also, you get your copy of E-book, signed acceptance certificate & diploma.</p>
                    </Grid>
                </Grid>
            </Grid>
        </Container>
    )
}

const Sections = () => {
    const { salon } = useSalonContext()

    return (
        <Container id="sections">
            <Grid>
                <h2>Sections</h2>
                <Grid gap="45px" columns="2">
                    {salon.state.sections.map(section => <Section key={section.id} {...section} />)}
                </Grid>
            </Grid>
        </Container>
    )
}

const Judges = () => {
    const { salon } = useSalonContext()

    const columns = getColumnsPropByCount(salon.state.juries.length)

    return (
        <Container id="judges">
            <Grid>
                <h2>Judges</h2>
                <Grid columns={columns}>
                    {salon.state.juries.map(jury => (
                        <Grid key={jury.id} gap="10px">
                            <AspectRatio width={1} height={1}>
                                <JudgeAvatar background={jury.avatar_url} />
                            </AspectRatio>
                            <h3>{formatJuryName(jury)}</h3>
                        </Grid>
                    ))}
                </Grid>
            </Grid>
        </Container>
    )
}

const About = () => {
    return (
        <Container id="about">
            <Grid>
                <h2>About Us</h2>
                <Grid columns="2" gap="30px|60px">
                    <Grid columns="2">
                        <Grid gap="10px">
                            <h3>Medals delivery guarantee</h3>
                            <p>We always send all medals & other awards on time however sometimes post can lose your awards. In this case please email us and we will send you a new award.</p>
                        </Grid>
                        <Grid gap="10px">
                            <h3>Everything on time</h3>
                            <p>We try to make everything on time. This is our main priority. We have not announced results later than on the day of notification. But we try to make it as earlier as possible.</p>
                        </Grid>
                        <Grid gap="10px">
                            <h3>We answer all emails</h3>
                            <p>We try to answer all emails as quick as possible. Usually during 1 working day. Sometimes we need some time to process your request. Our time zone is GMT+2.</p>
                        </Grid>
                        <Grid gap="10px">
                            <h3>We do not send spam</h3>
                            <p>We know that you do not like getting spam. Therefore we have developed our own email policy that is the same for every single salon we organize.</p>
                        </Grid>
                    </Grid>
                    <Support />
                </Grid>
            </Grid>
        </Container>
    )
}

const CallToAction = () => {
    const { salon } = useSalonContext()

    switch (salon.state.type) {
        case 'future':
        case 'current':
            return <Participate />
        case 'voting':
            return <SignIn />
        case 'finished':
            return <Results />
        default:
            throw new UnexpectedSalonTypeError(salon.state.type)
    }
}

const Results = () => {
    const { salon } = useSalonContext()

    return (
        <ParticipateContainer id="participate">
            <Grid>
                <Grid gap="10px">
                    <h2>Results</h2>
                    <p>It's a finished salon. Log in to your account to see your acceptances & awards.</p>
                </Grid>
                <Flex gap="10px">
                    <Button width="fixed" to={route("customer:salon", salon.state.id)}>
                        My Results
                    </Button>
                    <Button type="secondary" width="auto" to={route("general:salon:gallery", salon.state.slug)}>
                        Gallery
                    </Button>
                </Flex>
            </Grid>
        </ParticipateContainer>
    )
}

const SignIn = () => {
    return (
        <ParticipateContainer id="participate">
            <Grid>
                <Grid gap="10px">
                    <h2>Sign in</h2>
                    <p>It's an ongoing salon. Sign in to see your entry ot to participate in other open salons we have.</p>
                </Grid>
                <Flex gap="10px">
                    <Button type="secondary" width="fixed" to={route("signin")}>
                        Sign in
                    </Button>
                </Flex>
            </Grid>
        </ParticipateContainer>
    )
}

const Participate = () => {
    const { salon } = useSalonContext()

    return (
        <ParticipateContainer id="participate">
            <Grid>
                <Grid gap="10px">
                    <h2>Participate</h2>
                    <p>The salon is {salon.attrs.organizations().string} recognized. By participating you're agreeing to salon's conditions as well as to mentioned organizations rules.</p>
                </Grid>
                <Flex gap="10px">
                    <Button width="fixed" to={route("customer:salon", salon.state.id)}>
                        Participate
                    </Button>
                    <Button type="secondary" to={route("general:salon:conditions", salon.state.slug)} width="auto">
                        Conditions
                    </Button>
                </Flex>
            </Grid>
        </ParticipateContainer>
    )
}


const Support = () => {
    return (
        <Grid id="support">
            <Grid gap="20px">
                <Grid gap="10px">
                    <h3>Support</h3>
                    <p>We care about your comfort. If you have any problems with a website or salons, report an issue and we'll fix it as soon as possible.</p>
                </Grid>
                <Button width="fixed" type="secondary" to={route("general:support")}>
                    Contact
                </Button>
            </Grid>
        </Grid>
    )
}

// STYLES
const OrganizationLogo = styled.div`
    img {
        max-height: 84px;
    }
`

const MainScreenWrapper = styled.div`
    min-height: 100vh;
    position: relative;
    width: 100%;
    display: grid;
    grid-template-rows: minmax(300px, 1fr) auto;

    ${desktop(css`
        grid-template-rows: 1fr;
        grid-template-columns: 50% 50%;
    `)}
`

const BackgroundWrapper = styled.div<{ background: string }>`
    background: ${props => `url(${props.background}) center no-repeat`};
    background-size: cover;
    width: 100%;
    height: 100%;
    margin-top: 100px;

    ${desktop(css`
        margin-top: 0;
        grid-column-start: 2;
        grid-row-start: 1;
    `)}
`

const MainScreenContainer = styled(Container)`
    display: flex;
    align-items: flex-end;
    background: ${colors.white};
    margin: 0;
    padding-top: ${grid.margins.phone};
    padding-bottom: ${grid.margins.phone};

    h1 {
        line-height: 100%;
    }

    ${desktop(css`
        margin-top: 0;
        padding-top: ${grid.margins.tablet};
        padding-bottom: ${grid.margins.tablet};
        width: 100%;
        height: 100vh;
    `)}
`

const HeaderWrapper = styled(Container)`
    position: absolute;
    top: 0;
    left: 0;
    height: 100px;
    width: 100%;
    background: ${colors.white};

    ${desktop(css`
        background: transparent;
    `)}
`

const JudgeAvatar = styled.div<{ background: string }>`
    width: 100%;
    height: 100%;
    background: ${props => `url(${props.background}) center no-repeat`};
    background-size: cover;
    border-radius: 10px;
`

const ParticipateContainer = styled(Container)`
    padding-top: ${grid.margins.phone};
    padding-bottom: ${grid.margins.phone};
    background: ${colors.greyBackground};

    margin-bottom: -${grid.margins.phone};
    ${tablet(css`
        padding-top: ${grid.margins.tablet};
        padding-bottom: ${grid.margins.tablet};
        margin-bottom: -${grid.margins.tablet};
    `)}

    p {
        max-width: 400px;
    }
`

const ButtonsContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 20px;

    ${tablet(css`
        grid-template-columns: 250px 150px;
    `)}
`

// FUNCTiONS
function getColumnsPropByCount(count: number): GridProps["columns"] {
    if (count % 5 === 0)
        return "5"
    else if (count % 4 === 0)
        return "4"
    else
        return "3"
}